import { CheckCheck, ClipboardCheck } from 'lucide-react';
import { useMemo } from 'react';

import { REVIEW_HISTORY_LIMIT } from '../api/review.api';
import { ReviewHistory } from '../components/ReviewHistory';
import { useReviewHistory } from '../hooks/useReviewHistory';
import { useReviewQueue } from '../hooks/useReviewQueue';
import { useAuth } from '@/app/providers/AuthProvider';
import { ProjectTable } from '@/features/projects';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingState } from '@/shared/components/LoadingState';
import { PageHeader } from '@/shared/components/PageHeader';
import { can } from '@/shared/constants/roles';
import { messages } from '@/shared/constants/messages';
import { toUserMessage } from '@/shared/lib/errors';

/**
 * A fila `/analise` (US-18, decisão 1). Sem guard de rota — ADR-012: um
 * líder que digitar a URL vê fila vazia, não erro, e não os projetos dos
 * outros. É esta página, e só ela, que decide não disparar as consultas.
 *
 * **Uma lista, sem seções** (decisões 15 e 25). As duas seções viraram uma
 * quando a reivindicação de análise saiu da esteira: um estado que durava vinte
 * minutos não merecia um estágio de pipeline, e sem `under_review` não sobra o
 * que separar. Com isso somem também a coluna "Situação" — que dizia "Enviado"
 * em todas as linhas —, o botão por linha e a frase "Em análise por…".
 *
 * **Clicar na linha não faz nada** — quem abre o projeto é o botão "Analisar"
 * da última coluna (`rowAction`). Nesta tela abrir é o começo de uma decisão,
 * e o clique largo da linha fazia isso acontecer por acidente ao varrer a
 * lista. Continua valendo que nenhum clique daqui dispara RPC.
 */
export function ReviewQueuePage() {
  const { currentUser } = useAuth();
  const isReviewer = Boolean(currentUser && can(currentUser.roles, 'reviewProject'));
  const queueQuery = useReviewQueue(isReviewer);
  const historyQuery = useReviewHistory(isReviewer);

  // US-18: do mais antigo para o mais recente. Sem "meus primeiro" — não
  // existe mais "meu" (decisão 25).
  const queue = useMemo(() => {
    const items = queueQuery.data ?? [];
    return [...items].sort((a, b) => compareByDate(a.submitted_at, b.submitted_at));
  }, [queueQuery.data]);

  const history = historyQuery.data ?? [];

  return (
    <div className="space-y-8">
      <PageHeader title={messages.review.queueTitle} description={messages.review.queueDescription} />

      {!isReviewer ? (
        <EmptyState
          icon={<ClipboardCheck className="size-5" />}
          title={messages.review.queueEmptyNotReviewer}
        />
      ) : queueQuery.isPending ? (
        <LoadingState label="Carregando fila…" />
      ) : queueQuery.error ? (
        <EmptyState title="Não foi possível carregar" description={toUserMessage(queueQuery.error)} />
      ) : (
        <div className="space-y-10">
          {queue.length === 0 ? (
            <QueueAllDone decisionCount={history.length} isLoading={historyQuery.isPending} />
          ) : (
            <ProjectTable
              projects={queue}
              showCoordination
              showStatus={false}
              urgencyBy="waiting"
              rowAction
              from="/analise"
            />
          )}

          {/* O histórico continua abaixo mesmo com a fila zerada: é ele que dá
              lastro ao número da comemoração — ela afirma, a lista prova. */}
          <ReviewHistory
            entries={history}
            isLoading={historyQuery.isPending}
            currentProfileId={currentUser?.profile.id}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Fila zerada (decisão 31) — **a única tela do painel que merece comemoração**.
 *
 * Ela era uma frase de 14px dentro de uma caixa tracejada, e a moldura
 * tracejada é a linguagem de *"faltou conteúdo aqui"*: aqui não faltou nada,
 * está feito.
 *
 * ⚠️ **O elogio só aparece quando houve trabalho.** Instalação nova, ninguém
 * decidiu nada, fila vazia por construção — "Tudo em dia" ali é elogio vazio, e
 * elogio vazio ensina a ignorar a tela. Por isso o segundo estado existe:
 * mesma tela, sem comemoração, dizendo o que vai acontecer.
 *
 * **A frase é da EQUIPE, não sua**: as decisões dos 30 dias são de todos os
 * revisores no escopo (decisão 28), e escrever "você decidiu" seria mentira
 * para quem chegou depois do trabalho dos outros.
 */
function QueueAllDone({ decisionCount, isLoading }: { decisionCount: number; isLoading: boolean }) {
  // Enquanto o histórico carrega, a versão sóbria: comemorar e depois descobrir
  // que não houve decisão nenhuma seria pior do que esperar meio segundo.
  if (isLoading || decisionCount === 0) {
    return (
      <EmptyState
        icon={<ClipboardCheck className="size-5" />}
        title={messages.review.queueNeverDecidedTitle}
        description={messages.review.queueNeverDecidedDescription}
      />
    );
  }

  return (
    <EmptyState
      variant="hero"
      icon={<CheckCheck className="size-6" />}
      title={messages.review.queueAllDoneTitle}
      // O número NÃO custa consulta: sai do tamanho do histórico que a seção
      // logo abaixo já carregou.
      description={messages.review.queueAllDoneDescription(
        Math.min(decisionCount, REVIEW_HISTORY_LIMIT),
        decisionCount > REVIEW_HISTORY_LIMIT,
      )}
    />
  );
}

function compareByDate(a: string | null | undefined, b: string | null | undefined): number {
  if (!a || !b) return 0;
  return new Date(a).getTime() - new Date(b).getTime();
}
